import React, { CSSProperties } from 'react';
import styled, { css, useTheme } from 'styled-components';
import {
  display,
  DisplayProps,
  FlexProps,
  fontFamily,
  FontFamilyProps,
  fontSize,
  FontSizeProps,
  fontWeight,
  FontWeightProps,
  PositionProps,
  SizeProps,
  space,
  SpaceProps,
  themeGet
} from 'styled-system';
import { Button as BlueprintButton, ButtonProps as BlueprintButtonProps, Classes } from '@blueprintjs/core';
import classNames from 'classnames';

import { get } from 'lodash';
import { Property } from 'csstype';

import { formAriaProps, guessByChildText, guessTextOnIcon } from 'core/util/ariaUtils';
import { omitAriaProps } from 'core/form/components/util/omitFieldProps';
import Icon from './Icon';
import position from './utils/position';
import flex from './utils/flex';
import size from './utils/size';

interface StyledButtonProps
  extends
    Omit<BlueprintButtonProps, 'rightIcon' | 'icon'>,
    DisplayProps,
    FontFamilyProps,
    FontSizeProps,
    FontWeightProps,
    SpaceProps,
    PositionProps,
    FlexProps,
    SizeProps {
  ellipsis?: boolean;
  ref?: React.Ref<HTMLElement>;
  rightIcon?: React.ReactNode;
  icon?: React.ReactNode;
  style?: CSSProperties;
  title?: React.ReactNode;
}

const StyledButton: (props: StyledButtonProps) => JSX.Element = styled(BlueprintButton)<StyledButtonProps>`
  &.${Classes.BUTTON} {
    ${display};
    ${fontFamily};
    ${fontSize};
    ${fontWeight};
    ${space};

    ${position};
    ${flex};
    ${size};

    ${({ theme, small }) =>
      small &&
      css`
        font-size: ${theme?.fontSizes.small};

        .bp4-icon {
          &:first-child {
            margin-right: 4px;
          }

          &:first-child:last-child {
            margin: 0 -7px;
          }
        }
      `}
    > .${Classes.BUTTON_TEXT} {
      overflow: hidden;
      flex: 1 1 auto;
      text-align: left;
      color: ${({ color }) => themeGet(`colors.${color}`, color)};
    }

    &.align-center {
      > .${Classes.BUTTON_TEXT} {
        text-align: center;
      }
    }

    ${(props) =>
      props.ellipsis &&
      css`
        > .${Classes.BUTTON_TEXT} {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}
  }
`;

export type ButtonClickEvent = (e: React.MouseEvent<HTMLElement>) => void;

export interface ButtonProps extends StyledButtonProps {
  active?: boolean;
  activeBg?: string;
  alignSelf?: Property.AlignSelf;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaHaspopup?: boolean;
  onSpaceOrEnter?: (e: KeyboardEvent) => void;
  border?: Property.Border;
  minHeight?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  borderRadius?: string;
  className?: string;
  color?: string;
  iconColor?: string;
  explicitTabIndex?: number;
  large?: boolean;
  loading?: boolean;
  small?: boolean;
  justifyContent?: Property.JustifyContent;
  shouldDismissPopover?: boolean;
  iconSize?: number;
  width?: string | number;
  height?: string | number;
  textAlign?: Property.TextAlign;
  textColor?: string;
  title?: string;
  staticContext?: object;
  bg?: string;
  flexDirection?: Property.FlexDirection;
  alignItems?: Property.AlignItems;
  flexWrap?: Property.FlexWrap;
  tracker?: string;
  text?: React.ReactNode;
  ariaSelected?: boolean;
  name?: string;
  value?: string;
  role?: string;
  'aria-label'?: string;
  style?: React.CSSProperties;
  fontFamily?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  onClick?: ButtonClickEvent;
  minimal?: boolean;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
}

const ButtonComponent = (props: ButtonProps, ref: React.Ref<HTMLElement>): JSX.Element => {
  // destructure minWidth and alignSelf, otherwise we get non-standard prop warnings from React.
  const {
    active,
    activeBg,
    alignSelf,
    ariaLabel,
    ariaExpanded,
    ariaHaspopup = false,
    onSpaceOrEnter,
    border,
    minHeight,
    minWidth,
    maxWidth,
    maxHeight,
    borderRadius,
    className,
    color,
    icon,
    iconColor,
    rightIcon,
    large,
    loading,
    small,
    justifyContent,
    iconSize,
    width,
    height,
    textAlign = 'center',
    textColor,
    title,
    bg,
    flexDirection,
    alignItems,
    flexWrap,
    tracker,
    fontFamily: _fontFamily = 'body',
    // TODO these fields do nothing - are they missing functionality or are they safe to delete?
    shouldDismissPopover: _shouldDismissPopover,
    staticContext: _staticContext,

    ...rest
  } = props;

  const theme = useTheme();

  let finalIconSize = 16;
  if (iconSize) {
    finalIconSize = iconSize;
  } else if (large) {
    finalIconSize = 20;
  } else if (small) {
    finalIconSize = 12;
  }

  let background = bg;
  if (active && activeBg) {
    background = activeBg;
  }
  let ariaLabelGuess = rest.ellipsis ? rest.text : title;
  ariaLabelGuess =
    ariaLabelGuess ||
    rest.text ||
    guessByChildText(rest.children) ||
    (icon && guessTextOnIcon(icon)) ||
    (rightIcon && guessTextOnIcon(rightIcon));

  const ariaLabelText = ariaLabel || ariaLabelGuess;
  const buttonClassName = classNames(className, tracker, { 'align-center': textAlign === 'center' });

  const ariaProps = formAriaProps(
    props,
    {
      ariaLabel: ariaLabelText || rest.text,
      ariaPressed: active,
      ariaSelected: rest.ariaSelected || active,
      name: rest.name || rest.text || ariaLabel || rest['aria-label'],
      ariaHaspopup,
      ariaExpanded
    },
    {
      enableTabIndexNotDisabled: true,
      explicitTabIndex: rest.explicitTabIndex || 0
    }
  );

  if (rest.role) {
    // @ts-expect-error need to fix up the formAriaProps function
    ariaProps.role = rest.role;
  } else {
    // @ts-expect-error need to fix up the formAriaProps function
    ariaProps.role = 'button';
  }

  if (onSpaceOrEnter) {
    // @ts-expect-error this works but isn't really legal lol
    ariaProps.onKeyDown = (event: KeyboardEvent) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        onSpaceOrEnter(event);
      }
    };
  }

  return (
    <StyledButton
      // rest has a tabIndex=undefined so it has to go first
      {...omitAriaProps(rest)}
      {...ariaProps}
      active={active}
      large={large}
      loading={loading}
      small={small}
      className={buttonClassName}
      style={{
        alignSelf,
        alignItems,
        flexWrap,
        border,
        minHeight,
        minWidth,
        maxWidth,
        maxHeight,
        borderRadius,
        justifyContent,
        flexDirection,
        width,
        height,
        backgroundColor: background ? get(theme?.colors, background) : undefined,
        color: (textColor && get(theme?.colors, textColor)) || (color && get(theme?.colors, color)) || undefined,
        ...(rest.style || {})
      }}
      title={rest.ellipsis ? rest.text : title}
      icon={
        icon ? (
          <Icon icon={icon} color={iconColor || color} disabled={rest?.disabled} iconSize={finalIconSize} />
        ) : undefined
      }
      rightIcon={rightIcon ? <Icon icon={rightIcon} color={iconColor || color} iconSize={finalIconSize} /> : undefined}
      ref={ref}
    />
  );
};

const Button = React.forwardRef<HTMLElement, ButtonProps>(ButtonComponent);
Button.displayName = 'Button';

export default Button;
